import { useContext } from 'react';
import {
  useTranslation as useTranslationDefault,
  TFunction
} from 'react-i18next';
import { TranslationsContext } from '@core/components';

interface UseTranslationOpts {
  fallbackCb?: (tlPre: string) => string;
  skipPre?: boolean;
}

export type CustomTLFn = (
  translation: string,
  opts?: UseTranslationOpts
) => string;

interface TlFnsObj {
  t: CustomTLFn;
}

const logTlNotFoundInfo = (
  tlWithPre: string,
  loggedValues: React.MutableRefObject<string[]>
) => {
  if (loggedValues.current.includes(tlWithPre)) {
    return null;
  }

  loggedValues.current.push(tlWithPre);

  // eslint-disable-next-line no-console
  console.info(`TL not found - ${tlWithPre}`);
};

const checkIfTlNotFound = (
  tlWithPre: string,
  translation: string,
  t: TFunction<'translation'>
) => {
  const testFallback = '__TEST_FALLBACK';

  const moduleTl = t(tlWithPre, testFallback);
  const coreTl = t(translation, testFallback);

  return moduleTl === testFallback && coreTl === testFallback;
};

export const useTranslation = (): TlFnsObj => {
  const { t } = useTranslationDefault();

  const { translationsPre, loggedValues } = useContext(TranslationsContext);

  return {
    t: (translation, { fallbackCb, skipPre } = {}) => {
      const tlWithPre = `${skipPre ? '' : translationsPre}${translation}`;
      const fallback = t(translation);

      const translated = t(tlWithPre, fallback);

      if (checkIfTlNotFound(tlWithPre, translation, t) && !skipPre) {
        logTlNotFoundInfo(tlWithPre, loggedValues);

        return fallbackCb ? fallbackCb(translationsPre) : translated;
      }

      return translated;
    }
  };
};
